import type { ReactElement } from 'react';
import { Link } from 'react-router';
import type { Appointment, Client, ConversationSummary } from '@zenda/api-contract';
import { StatTile } from '../components/StatTile';
import { Skeleton, SkeletonRows } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { Badge } from '../components/Badge';
import { ClientTag } from '../components/ClientTag';
import { Icon } from '../components/Icon';
import { useTip } from '../components/Tip';
import { useAppointments, useClients, useConversations, useStats } from '../lib/queries';
import { byAttention, needsAttention, worstAlert } from '../lib/clients';
import {
  dataDeHoje,
  formatBrlRound,
  formatCountdown,
  formatRelative,
  formatTime,
  toDateInput,
} from '../lib/format';
import { aguardaResposta, janelaApertando } from '../lib/urgencia';
import type { Dictionary } from '../content/pt-BR';
import { useI18n } from '../lib/i18n';
import { appointmentStatus, nomeDoAtendido } from '../lib/labels';
import { rotaDaConversa } from '../lib/rotas';
import { Responsavel } from '../components/LinhaDeConversa';
import { useAuth } from '../lib/auth';
import { PilotoHoje } from '../components/PilotoNoPainel';
import '../styles/dashboard.css';

/**
 * O dia da operação, e não o dia do navegador.
 *
 * `toDateString()` compara no fuso de quem abriu a aba: com a atendente fora do país,
 * ou num navegador com fuso trocado, "Agenda de hoje" mostrava o dia errado inteiro.
 * `toDateInput` é a mesma conversão que o resto da interface usa.
 */
function isToday(iso: string): boolean {
  return toDateInput(iso) === dataDeHoje();
}

function urgency(c: ConversationSummary): number {
  if (!c.serviceWindow.open) return -1;
  return c.serviceWindow.minutesRemaining ?? Number.MAX_SAFE_INTEGER;
}

/** "Dra. Camila Ferraz: conexao caiu" — o motivo, nao so a contagem. */
function topIssue(t: Dictionary, client: Client): string {
  const worst = worstAlert(t, client);
  return worst === undefined ? client.name : `${client.name}: ${worst.title}`;
}

export function DashboardPage(): ReactElement {
  const { t, locale } = useI18n();
  const tip = useTip();
  const eu = useAuth().me?.userId ?? null;
  const stats = useStats();
  const conversations = useConversations();
  const appointments = useAppointments();
  const clients = useClients();

  const waiting = [...(conversations.data ?? [])]
    .filter(aguardaResposta)
    .sort((a, b) => urgency(a) - urgency(b));

  const today: readonly Appointment[] = [...(appointments.data ?? [])]
    .filter((a) => isToday(a.startsAt))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const attention = [...(clients.data ?? [])]
    .filter((c) => c.access === 'casa' && needsAttention(c))
    .sort(byAttention(t, locale));
  const semClientes = clients.data !== undefined && clients.data.length === 0;

  return (
    <div className="page page--painel">
      {attention.length > 0 ? (
        <Link to="/clientes" className="alert">
          <span className="alert__icon">
            <Icon name="alert" size={18} />
          </span>
          <div>
            <p className="alert__title">
              {attention.length === 1
                ? t.dashboard.oneNeedsAction
                : t.dashboard.manyNeedAction(attention.length)}
            </p>
            <p className="alert__text">
              {attention
                .slice(0, 3)
                .map((c) => topIssue(t, c))
                .join(' · ')}
              {attention.length > 3 ? t.dashboard.andMore(attention.length - 3) : ''}.
            </p>
          </div>
          <Icon name="chevron-right" size={18} />
        </Link>
      ) : null}

      {semClientes ? (
        <section className="card painel__comeco">
          <div>
            <h2 className="card__title">{t.dashboard.firstRunTitle}</h2>
            <p className="card__subtitle">{t.dashboard.firstRunText}</p>
          </div>
          <Link to="/clientes?novo=1" className="btn btn--primary btn--sm">
            <Icon name="plus" size={15} />
            {t.dashboard.firstRunCta}
          </Link>
        </section>
      ) : null}

      <section className="stats" aria-label={t.dashboard.indicators}>
        {stats.isPending || stats.data === undefined ? (
          <>
            <Skeleton h={86} radius="var(--radius-md)" />
            <Skeleton h={86} radius="var(--radius-md)" />
            <Skeleton h={86} radius="var(--radius-md)" />
            <Skeleton h={86} radius="var(--radius-md)" />
          </>
        ) : (
          <>
            <StatTile
              tone={stats.data.clientsNeedingAttention > 0 ? 'rose' : 'slate'}
              label={t.dashboard.clientsAttentionTitle}
              value={stats.data.clientsNeedingAttention}
              hint={t.dashboard.clientsAttentionHint}
              emphasis={stats.data.clientsNeedingAttention > 0}
              to="/clientes"
            />
            <StatTile
              tone={stats.data.windowsClosingSoon > 0 ? 'amber' : 'slate'}
              label={t.dashboard.windowsExpiringTitle}
              value={stats.data.windowsClosingSoon}
              hint={t.dashboard.windowsExpiringHint}
              to="/inbox"
            />
            <StatTile
              tone="slate"
              label={t.dashboard.conversationsWaiting}
              value={stats.data.awaitingReply}
              hint={t.dashboard.openTotalHint(stats.data.conversationsOpen)}
              to="/inbox"
            />
            <StatTile
              tone={stats.data.paymentsOverdue > 0 ? 'rose' : 'slate'}
              label={t.dashboard.overduePayments}
              value={stats.data.paymentsOverdue}
              hint={t.dashboard.overdueHint}
              to="/clientes"
            />
            {/* Receita e clientes ativos contam o negócio e não pedem nada: mesmo
                formato dos outros, sem tinta e com o número menor. Como texto solto
                embaixo da fileira eles pareciam sobra de layout. */}
            <div className="stat stat--negocio">
              <span className="stat__par">
                <span className="stat__value stat__value--sm num">
                  {formatBrlRound(stats.data.monthlyRevenueBrl)}
                </span>
                <span className="stat__label">{t.dashboard.monthlyRevenue}</span>
              </span>
              <span className="stat__par">
                <span className="stat__value stat__value--sm num">{stats.data.clientsActive}</span>
                <span className="stat__label">{t.dashboard.activeClients}</span>
              </span>
            </div>
          </>
        )}
      </section>

      <PilotoHoje />

      <section className="grid-2">
        <div className="card card--flush painel__col">
          <div className="painel__cab">
            <div>
              <h2 className="card__title">{t.dashboard.waitingTitle}</h2>
              <p className="card__subtitle">{t.dashboard.waitingSub}</p>
            </div>
            <Link to="/inbox" className="btn btn--ghost btn--sm">
              {t.dashboard.seeAll}
            </Link>
          </div>

          <div className="list painel__lista">
            {conversations.isPending ? (
              <div style={{ padding: 'var(--space-5) var(--space-6)' }}>
                <SkeletonRows rows={4} />
              </div>
            ) : waiting.length === 0 ? (
              <EmptyState
                icon="check"
                title={t.dashboard.waitingEmpty}
                text={
                  semClientes ? t.dashboard.waitingEmptyNoClients : t.dashboard.waitingEmptyText
                }
              />
            ) : (
              waiting.map((c) => {
                const w = c.serviceWindow;
                const soon = janelaApertando(w);
                return (
                  <Link key={c.id} to={rotaDaConversa(c.id)} className="row">
                    <span className="rail" style={{ background: c.clientColor }} />
                    <div className="row__main">
                      <p className="row__title">{c.contactName}</p>
                      <p className="row__sub row__sub--tag">
                        <ClientTag name={c.clientName} color={c.clientColor} variant="plain" />
                        <span className="row__preview">
                          {c.lastMessagePreview ?? t.inbox.noMessages}
                        </span>
                      </p>
                    </div>
                    <div className="row__side">
                      {!w.open ? (
                        <Badge tone="rose">{t.dashboard.windowClosed}</Badge>
                      ) : (
                        <Badge tone={soon ? 'amber' : 'mint'}>
                          {w.minutesRemaining === null
                            ? t.inbox.windowOpenFlag
                            : t.dashboard.windowLeft(formatCountdown(w.minutesRemaining))}
                        </Badge>
                      )}
                      <span className="row__time num">
                        {c.lastMessageAt === null ? '—' : formatRelative(c.lastMessageAt)}
                      </span>
                      {c.assigneeId === null || c.assigneeName === null ? null : (
                        <Responsavel
                          id={c.assigneeId}
                          nome={c.assigneeName}
                          eu={c.assigneeId === eu}
                        />
                      )}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        <div className="card card--flush painel__col">
          <div className="painel__cab">
            <div>
              <h2 className="card__title">{t.dashboard.todayTitle}</h2>
              <p className="card__subtitle">{t.dashboard.appointmentCount(today.length)}</p>
            </div>
            <Link to="/agenda" className="btn btn--ghost btn--sm">
              {t.dashboard.openAgenda}
            </Link>
          </div>

          <div className="list painel__lista">
            {appointments.isPending ? (
              <div style={{ padding: 'var(--space-5) var(--space-6)' }}>
                <SkeletonRows rows={4} height={48} />
              </div>
            ) : today.length === 0 ? (
              <EmptyState
                icon="calendar"
                title={t.dashboard.todayEmpty}
                text={t.dashboard.todayEmptyText}
              />
            ) : (
              today.map((a) => (
                <div
                  key={a.id}
                  className="row row--static"
                  /* O tema da consulta fica no hover, como na agenda do dia.
                     `title` é dado de saúde (LGPD nota ⁴: pode conter procedimento ou
                     queixa), e este painel é a lista mais larga que existe — todos os
                     médicos, o dia inteiro, na tela que fica aberta no balcão. O nome
                     da paciente ao lado do procedimento impresso é o que a nota ⁸ já
                     recusou na agenda. */
                  {...(a.title === null ? {} : tip(a.title))}
                >
                  <span className="row__time num row__time--lead">{formatTime(a.startsAt)}</span>
                  <span className="rail" style={{ background: a.doctorColor }} />
                  <div className="row__main">
                    <p className="row__title">{nomeDoAtendido(t, a)}</p>
                    <p className="row__sub">{a.doctorName}</p>
                  </div>
                  <Badge tone={appointmentStatus(t, a.status).tone}>
                    {appointmentStatus(t, a.status).text}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
